/**
 * How the admin lists that load whole (the Content Hub lists and Feature
 * votes) sort in the browser: the columns each list offers, the address
 * params that carry the sort, and the row comparison. The page sorts what
 * the server returned.
 *
 * Aliases: content hub sort, cih list sort, admin list order, sort by date, sort by title
 */

import { z } from "zod";

import {
  SORT_DIRECTIONS,
  type SortDefaults,
  type SortDirection,
} from "~/lib/shared/sort";

/**
 * The `sort` and `dir` address params of a list with these sort columns. A
 * stale or hand-edited link drops the params it gets wrong instead of failing
 * the page: `?sort=nonsense` shows the default order.
 */
export function listSearchSchema<Sorts extends readonly [string, ...string[]]>(
  sorts: Sorts,
) {
  return z.object({
    sort: z.enum(sorts).optional().catch(undefined),
    dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
  });
}

/**
 * Text columns start A-Z; the columns in `newestFirst` start with the newest
 * or largest row. `defaultSort` is the order the list opens in.
 */
export function listSortDefaults<Sort extends string>(
  defaultSort: Sort,
  newestFirst: readonly Sort[] = [],
): SortDefaults<Sort> {
  return {
    sort: defaultSort,
    direction: (sort) => (newestFirst.includes(sort) ? "desc" : "asc"),
  };
}

/** What one sort column reads from a row. */
export type SortColumn<Row> =
  | { kind: "text"; value: (row: Row) => string }
  | { kind: "number"; value: (row: Row) => number }
  | { kind: "date"; value: (row: Row) => Date | string | null };

// One fixed locale so the server render and every browser agree; base
// sensitivity ignores accents and capitals the way the alphabet does.
const nameCollator = new Intl.Collator("en", { sensitivity: "base" });

/** The time of a date value, or undefined when the row has no usable date. */
function timeOf(value: Date | string | null): number | undefined {
  if (value === null) return undefined;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? undefined : time;
}

/**
 * The rows in the requested order, as a new array. Rows without a date go last
 * in both directions. Rows that tie keep A-Z order by `name`, whatever the
 * direction.
 */
export function sortListRows<Row, Sort extends string>(
  rows: readonly Row[],
  sort: Sort,
  direction: SortDirection,
  columns: Readonly<Record<Sort, SortColumn<Row>>>,
  name: (row: Row) => string,
): Row[] {
  const sign = direction === "asc" ? 1 : -1;
  const column = columns[sort];
  const byName = (left: Row, right: Row): number =>
    nameCollator.compare(name(left), name(right));

  return [...rows].sort((left, right) => {
    if (column.kind === "text") {
      return (
        sign * nameCollator.compare(column.value(left), column.value(right)) ||
        byName(left, right)
      );
    }
    if (column.kind === "number") {
      return (
        sign * (column.value(left) - column.value(right)) || byName(left, right)
      );
    }
    const leftTime = timeOf(column.value(left));
    const rightTime = timeOf(column.value(right));
    if (leftTime === undefined || rightTime === undefined) {
      if (leftTime === rightTime) return byName(left, right);
      return leftTime === undefined ? 1 : -1;
    }
    return sign * (leftTime - rightTime) || byName(left, right);
  });
}
